import axios from 'axios';
import { APIClient } from '../../../helpers/api_helper';
import type {
  Employee,
  Profile,
  Attendance,
  AttendancePolicy,
  Document,
  DocumentLibrary,
  OnboardingProgress,
  OffboardingProgress,
  OnboardingTemplate,
  DashboardStats,
} from '../types';

const apiClient = new APIClient();
const HR_API_BASE = '/hrapi';

// Helper to extract error message from axios error response
// Handles both error.response.data.error and error.response.error (depending on axios config)
export const extractErrorMessage = (error: any): string => {
  if (!error) return 'Unknown error';
  
  // Check error.response.data.error (standard axios structure)
  if (error.response?.data?.error) {
    return error.response.data.error;
  }
  
  // Check error.response.error (if response body is directly on response)
  if (error.response?.error) {
    return error.response.error;
  }
  
  // Check error.response.data (if error is the whole data object)
  if (error.response?.data && typeof error.response.data === 'string') {
    return error.response.data;
  }
  
  // Fallback to error.message
  return error.message || 'Unknown error';
};

// Helper to get auth token from localStorage (exported for Checkr embed sessionTokenRequestHeaders)
// JWT auth stores token in 'access_token'; fake/other auth may use authUser.token or authUser.access
export const getAuthToken = (): string | null => {
  const accessToken = localStorage.getItem('access_token');
  if (accessToken) return accessToken;
  const authUser = localStorage.getItem('authUser');
  if (authUser) {
    try {
      const user = JSON.parse(authUser);
      return user?.token || user?.access || user?.accessToken || null;
    } catch {
      return null;
    }
  }
  return null;
};

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ==================== Auth API ====================
export const hrAuthApi = {
  getUser: async () => {
    const response = await apiClient.get(`${HR_API_BASE}/auth/user/`, null);
    return response.data || response;
  },
};

// ==================== Employee API ====================
export const employeeApi = {
  // Get all employees with optional filters
  getEmployees: async (params?: {
    status?: string;
    department?: string;
    position?: string;
    search?: string;
    ordering?: string;
  }) => {
    const response = await apiClient.get(`${HR_API_BASE}/employees/`, params);
    return response.data || response;
  },

  // Get single employee
  getEmployee: async (id: string) => {
    const response = await apiClient.get(`${HR_API_BASE}/employees/${id}/`, null);
    return response.data || response;
  },

  // Create employee
  createEmployee: async (data: Partial<Employee>) => {
    const response = await apiClient.create(`${HR_API_BASE}/employees/`, data);
    return response.data || response;
  },

  // Update employee (partial)
  updateEmployee: async (id: string, data: Partial<Employee>) => {
    const response = await apiClient.update(`${HR_API_BASE}/employees/${id}/`, data);
    return response.data || response;
  },

  // Update employee (full)
  putEmployee: async (id: string, data: Partial<Employee>) => {
    const response = await apiClient.put(`${HR_API_BASE}/employees/${id}/`, data);
    return response.data || response;
  },

  // Delete employee
  deleteEmployee: async (id: string) => {
    const response = await apiClient.delete(`${HR_API_BASE}/employees/${id}/`, {});
    return response.data || response;
  },

  // Update employee status
  updateEmployeeStatus: async (id: string, status: string) => {
    const response = await apiClient.update(`${HR_API_BASE}/employees/${id}/update_status/`, { status });
    return response.data || response;
  },

  // Get dashboard stats
  getDashboardStats: async (): Promise<DashboardStats> => {
    const response = await apiClient.get(`${HR_API_BASE}/employees/dashboard_stats/`, null);
    return response.data || response;
  },

  // Profile APIs
  getProfiles: async () => {
    const response = await apiClient.get(`${HR_API_BASE}/employees/profiles/`, null);
    return response.data || response;
  },

  getProfile: async (id: string) => {
    const response = await apiClient.get(`${HR_API_BASE}/employees/profiles/${id}/`, null);
    return response.data || response;
  },

  getMyProfile: async () => {
    const response = await apiClient.get(`${HR_API_BASE}/employees/profiles/me/`, null);
    return response.data || response;
  },

  createProfile: async (data: Partial<Profile>) => {
    const response = await apiClient.create(`${HR_API_BASE}/employees/profiles/`, data);
    return response.data || response;
  },

  updateProfile: async (id: string, data: Partial<Profile>) => {
    const response = await apiClient.update(`${HR_API_BASE}/employees/profiles/${id}/`, data);
    return response.data || response;
  },

  // Get employee documents
  getEmployeeDocuments: async (employeeId: string) => {
    const response = await apiClient.get(`${HR_API_BASE}/employees/documents/`, { employee_id: employeeId });
    return response.data || response;
  },

  // Delete employee document
  deleteEmployeeDocument: async (documentId: string) => {
    const response = await apiClient.delete(`${HR_API_BASE}/employees/documents/${documentId}/`, {});
    return response.data || response;
  },
};

// ==================== Document API ====================
export const documentApi = {
  // Get all documents
  getDocuments: async (params?: {
    employee?: string;
    document_type?: string;
    status?: string;
  }) => {
    const response = await apiClient.get(`${HR_API_BASE}/documents/`, params);
    return response.data || response;
  },

  // Get single document
  getDocument: async (id: string) => {
    const response = await apiClient.get(`${HR_API_BASE}/documents/${id}/`, null);
    return response.data || response;
  },

  // Create document
  createDocument: async (data: Partial<Document>) => {
    const response = await apiClient.create(`${HR_API_BASE}/documents/`, data);
    return response.data || response;
  },

  // Update document
  updateDocument: async (id: string, data: Partial<Document>) => {
    const response = await apiClient.update(`${HR_API_BASE}/documents/${id}/`, data);
    return response.data || response;
  },

  // Delete document
  deleteDocument: async (id: string) => {
    const response = await apiClient.delete(`${HR_API_BASE}/documents/${id}/`, {});
    return response.data || response;
  },

  // Update document status
  updateDocumentStatus: async (id: string, status: string) => {
    const response = await apiClient.update(`${HR_API_BASE}/documents/${id}/update_status/`, { status });
    return response.data || response;
  },

  // Get documents by employee
  getDocumentsByEmployee: async (employeeId: string) => {
    const response = await apiClient.get(`${HR_API_BASE}/documents/by_employee/`, { employee: employeeId });
    return response.data || response;
  },

  // Check signature status
  checkSignatureStatus: async (data: { employee_id: string }) => {
    const response = await apiClient.get(`${HR_API_BASE}/documents/check_signature_status/`, data);
    return response.data || response;
  },

  // Document Library APIs
  getDocumentLibrary: async () => {
    const response = await apiClient.get(`${HR_API_BASE}/documents/library/`, null);
    return response.data || response;
  },

  getDocumentLibraryItem: async (id: string) => {
    const response = await apiClient.get(`${HR_API_BASE}/documents/library/${id}/`, null);
    return response.data || response;
  },

  createDocumentLibraryItem: async (data: Partial<DocumentLibrary>) => {
    const response = await apiClient.create(`${HR_API_BASE}/documents/library/`, data);
    return response.data || response;
  },

  updateDocumentLibraryItem: async (id: string, data: Partial<DocumentLibrary>) => {
    const response = await apiClient.update(`${HR_API_BASE}/documents/library/${id}/`, data);
    return response.data || response;
  },

  deleteDocumentLibraryItem: async (id: string) => {
    const response = await apiClient.delete(`${HR_API_BASE}/documents/library/${id}/`, {});
    return response.data || response;
  },
};

// ==================== Attendance API ====================
export const attendanceApi = {
  // Get all attendance records
  getAttendance: async (params?: {
    employee?: string;
    date?: string;
    status?: string;
  }) => {
    const response = await apiClient.get(`${HR_API_BASE}/attendance/`, params);
    return response.data || response;
  },

  // Get single attendance record
  getAttendanceRecord: async (id: number) => {
    const response = await apiClient.get(`${HR_API_BASE}/attendance/${id}/`, null);
    return response.data || response;
  },

  // Create attendance record
  createAttendance: async (data: Partial<Attendance>) => {
    const response = await apiClient.create(`${HR_API_BASE}/attendance/`, data);
    return response.data || response;
  },

  // Update attendance record
  updateAttendance: async (id: number, data: Partial<Attendance>) => {
    const response = await apiClient.update(`${HR_API_BASE}/attendance/${id}/`, data);
    return response.data || response;
  },

  // Delete attendance record
  deleteAttendance: async (id: number) => {
    const response = await apiClient.delete(`${HR_API_BASE}/attendance/${id}/`, {});
    return response.data || response;
  },

  // Get attendance by employee
  getAttendanceByEmployee: async (params: { employee_id: string; start_date?: string; end_date?: string }) => {
    const response = await apiClient.get(`${HR_API_BASE}/attendance/by_employee/`, params);
    return response.data || response;
  },

  // Get dashboard stats (today's stats)
  getAttendanceStats: async () => {
    const response = await apiClient.get(`${HR_API_BASE}/attendance/dashboard_stats/`, null);
    return response.data || response;
  },

  // Get monthly report
  getMonthlyReport: async (params: { employee_id: string; month: string }) => {
    const response = await apiClient.get(`${HR_API_BASE}/attendance/monthly_report/`, params);
    return response.data || response;
  },

  // Attendance Policy APIs
  getAttendancePolicies: async () => {
    const response = await apiClient.get(`${HR_API_BASE}/attendance/policies/`, null);
    return response.data || response;
  },

  getAttendancePolicy: async (id: number) => {
    const response = await apiClient.get(`${HR_API_BASE}/attendance/policies/${id}/`, null);
    return response.data || response;
  },

  createAttendancePolicy: async (data: Partial<AttendancePolicy>) => {
    const response = await apiClient.create(`${HR_API_BASE}/attendance/policies/`, data);
    return response.data || response;
  },

  updateAttendancePolicy: async (id: number, data: Partial<AttendancePolicy>) => {
    const response = await apiClient.update(`${HR_API_BASE}/attendance/policies/${id}/`, data);
    return response.data || response;
  },
};

// ==================== Onboarding API ====================
export const onboardingApi = {
  // Get all onboarding progress
  getOnboardingProgress: async (params?: { employee?: string }) => {
    const response = await apiClient.get(`${HR_API_BASE}/onboarding/progress/`, params);
    return response.data || response;
  },

  // Get single onboarding progress
  getOnboardingProgressItem: async (id: string) => {
    const response = await apiClient.get(`${HR_API_BASE}/onboarding/progress/${id}/`, null);
    return response.data || response;
  },

  // Create onboarding progress
  createOnboardingProgress: async (data: Partial<OnboardingProgress>) => {
    const response = await apiClient.create(`${HR_API_BASE}/onboarding/progress/`, data);
    return response.data || response;
  },

  // Update onboarding progress
  updateOnboardingProgress: async (id: string, data: Partial<OnboardingProgress>) => {
    const response = await apiClient.update(`${HR_API_BASE}/onboarding/progress/${id}/`, data);
    return response.data || response;
  },

  // Save onboarding progress
  saveOnboardingProgress: async (data: { employee_id: string; step_name: string; completed: boolean }) => {
    const response = await apiClient.create(`${HR_API_BASE}/onboarding/progress/save_progress/`, data);
    return response.data || response;
  },

  // Get onboarding progress by employee
  getOnboardingProgressByEmployee: async (employeeId: string) => {
    const response = await apiClient.get(`${HR_API_BASE}/onboarding/progress/by_employee/`, { employee: employeeId });
    return response.data || response;
  },

  // Check completion
  checkCompletion: async (data: { employee_id: string }) => {
    const response = await apiClient.create(`${HR_API_BASE}/onboarding/progress/check_completion/`, data);
    return response.data || response;
  },

  // Onboarding Template APIs
  getOnboardingTemplates: async () => {
    const response = await apiClient.get(`${HR_API_BASE}/onboarding/templates/`, null);
    return response.data || response;
  },

  getOnboardingTemplate: async (id: string) => {
    const response = await apiClient.get(`${HR_API_BASE}/onboarding/templates/${id}/`, null);
    return response.data || response;
  },

  createOnboardingTemplate: async (data: Partial<OnboardingTemplate>) => {
    const response = await apiClient.create(`${HR_API_BASE}/onboarding/templates/`, data);
    return response.data || response;
  },

  updateOnboardingTemplate: async (id: string, data: Partial<OnboardingTemplate>) => {
    const response = await apiClient.update(`${HR_API_BASE}/onboarding/templates/${id}/`, data);
    return response.data || response;
  },

  // Upload onboarding document
  uploadDocument: async (formData: FormData) => {
    const response = await apiClient.create(
      `${HR_API_BASE}/onboarding/upload-document/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data || response;
  },

  // Generate onboarding document
  generateDocument: async (data: {
    employee_id: string;
    document_type: string;
    employee_data: any;
  }) => {
    const response = await apiClient.create(`${HR_API_BASE}/onboarding/generate-document/`, data);
    return response.data || response;
  },

  // Get document download URL (pre-signed)
  getDocumentDownloadUrl: async (fileKey: string, expiresIn: number = 3600) => {
    const response = await apiClient.get(`${HR_API_BASE}/onboarding/get-document-url/`, {
      fileKey,
      expiresIn,
    });
    return response.data || response;
  },

  // Send document package via email
  sendDocumentPackage: async (data: {
    employee_id: string;
    recipient_email: string;
    document_keys: string[];
  }) => {
    const response = await apiClient.create(`${HR_API_BASE}/onboarding/send-package/`, data);
    return response.data || response;
  },

  // Send document package via DocuSign
  sendDocuSignPackage: async (data: {
    action: string;
    employeeId: string;
    employeeName: string;
    employeeEmail: string;
    documents: Array<{ name: string; fileKey: string }>;
    documentTabs: any[];
  }) => {
    const response = await apiClient.create(
      `${HR_API_BASE}/onboarding/send-docusign-package/`,
      data
    );
    return response.data || response;
  },
};

// ==================== Offboarding API ====================
export const offboardingApi = {
  // Save offboarding progress for a step
  saveOffboardingProgress: async (data: {
    employee_id: string;
    step_name: string;
    completed: boolean;
  }) => {
    const response = await apiClient.create(`${HR_API_BASE}/offboarding/progress/save_progress/`, data);
    return response.data || response;
  },

  // Get offboarding progress by employee
  getOffboardingProgress: async (employee_id: string) => {
    const response = await apiClient.get(`${HR_API_BASE}/offboarding/progress/by_employee/`, {
      employee: employee_id,
    });
    return response.data || response;
  },

  // Check if all offboarding steps are completed
  checkOffboardingCompletion: async (employee_id: string) => {
    const response = await apiClient.create(`${HR_API_BASE}/offboarding/progress/check_completion/`, {
      employee_id,
    });
    return response.data || response;
  },

  // Upload offboarding document (termination letter)
  uploadDocument: async (formData: FormData) => {
    const response = await apiClient.create(`${HR_API_BASE}/offboarding/upload-document/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data || response;
  },

  // Get document download URL (pre-signed)
  getDocumentDownloadUrl: async (fileKey: string, expiresIn: number = 3600) => {
    const response = await apiClient.get(`${HR_API_BASE}/offboarding/get-document-url/`, {
      fileKey,
      expiresIn,
    });
    return response.data || response;
  },
};

// ==================== DocuSign API ====================
export const docusignApi = {
  // Get DocuSign OAuth authorization URL from backend (Best Practice)
  connect: async () => {
    const response = await apiClient.get(`${HR_API_BASE}/integrations/docusign/connect/`, null);
    return response.data || response;
  },

  // Handle DocuSign OAuth callback
  callback: async (code: string) => {
    const response = await apiClient.create(`${HR_API_BASE}/integrations/docusign/callback/`, {
      code,
    });
    return response.data || response;
  },

  // Check DocuSign connection status
  checkStatus: async () => {
    const response = await apiClient.get(`${HR_API_BASE}/integrations/docusign/check-status/`, null);
    return response.data || response;
  },

  // Refresh DocuSign access token
  refreshToken: async () => {
    const response = await apiClient.create(`${HR_API_BASE}/integrations/docusign/refresh-token/`, {});
    return response.data || response;
  },

  // Create DocuSign envelope draft
  createEnvelopeDraft: async (data: {
    employee_id: string;
    envelope_type: string;
    document_ids: string[];
  }) => {
    const response = await apiClient.create(
      `${HR_API_BASE}/integrations/docusign/create-envelope-draft/`,
      data
    );
    return response.data || response;
  },

  // Get existing envelope draft
  getEnvelopeDraft: async (employee_id: string, envelope_type: string = 'employee-onboarding') => {
    const response = await apiClient.create(
      `${HR_API_BASE}/integrations/docusign/get-envelope-draft/`,
      { employee_id, envelope_type }
    );
    return response.data || response;
  },

  // Delete envelope draft
  deleteEnvelopeDraft: async (data: {
    employee_id: string;
    envelope_type: string;
  }) => {
    // Use query parameters for DELETE request
    const params = new URLSearchParams({
      employee_id: data.employee_id,
      envelope_type: data.envelope_type,
    });
    const response = await apiClient.delete(
      `${HR_API_BASE}/integrations/docusign/delete-envelope-draft/?${params.toString()}`,
      {}
    );
    return response.data || response;
  },

  // Get sender view URL for placing signature tabs
  getSenderView: async (envelope_id: string) => {
    const response = await apiClient.create(
      `${HR_API_BASE}/integrations/docusign/envelopes/${envelope_id}/sender-view/`,
      {}
    );
    return response.data || response;
  },

  // Check if envelope has tabs placed
  checkEnvelopeTabs: async (envelope_id: string) => {
    const response = await apiClient.create(
      `${HR_API_BASE}/integrations/docusign/envelopes/${envelope_id}/check-tabs/`,
      {}
    );
    return response.data || response;
  },

  // Resend envelope to recipients
  resendEnvelope: async (envelope_id: string) => {
    const response = await apiClient.create(
      `${HR_API_BASE}/integrations/docusign/envelopes/${envelope_id}/resend/`,
      {}
    );
    return response.data || response;
  },

  // Void envelope
  voidEnvelope: async (envelope_id: string, void_reason?: string) => {
    const response = await apiClient.create(
      `${HR_API_BASE}/integrations/docusign/envelopes/${envelope_id}/void/`,
      { void_reason: void_reason || 'Voided by user' }
    );
    return response.data || response;
  },

  // Check signature status of sent envelopes for an employee
  checkSignatureStatus: async (employee_id: string, envelope_type?: string) => {
    const params: any = { employee_id };
    if (envelope_type) {
      params.envelope_type = envelope_type;
    }
    const response = await apiClient.create(
      `${HR_API_BASE}/integrations/docusign/check-signature-status/`,
      params
    );
    return response.data || response;
  },
};

// ==================== Checkr API ====================
export const checkrApi = {
  checkStatus: async () => {
    const response = await apiClient.get(`${HR_API_BASE}/integrations/checkr/check-status/`, null);
    return response.data || response;
  },

  disconnect: async () => {
    const response = await apiClient.create(`${HR_API_BASE}/integrations/checkr/disconnect/`, {});
    return response.data || response;
  },

  /** Get the connected Checkr account and users (account name, company, users list) */
  getUser: async () => {
    const response = await apiClient.get(`${HR_API_BASE}/integrations/checkr/user/`, null);
    return response.data || response;
  },

  oauthInit: async () => {
    const response = await apiClient.get(`${HR_API_BASE}/integrations/checkr/oauth-init/`, null);
    return response.data || response;
  },

  getSessionToken: async () => {
    const response = await apiClient.create(`${HR_API_BASE}/integrations/checkr/session-token/`, {});
    return response.data || response;
  },

  getInviteConfig: async () => {
    const response = await apiClient.get(`${HR_API_BASE}/integrations/checkr/invite-config/`, null);
    return response.data || response;
  },

  saveInvitation: async (data: {
    employee_id: string;
    candidate_id: string;
    report_id?: string;
    invitation_id?: string;
  }) => {
    const response = await apiClient.create(
      `${HR_API_BASE}/integrations/checkr/save-invitation/`,
      data
    );
    return response.data || response;
  },

  /** Get whether a background check invitation was already sent for an employee */
  getInvitationStatus: async (employeeId: string) => {
    const response = await apiClient.get(
      `${HR_API_BASE}/integrations/checkr/invitation-status/`,
      { employee_id: employeeId }
    );
    return response.data ?? response;
  },

  /**
   * Refresh report status from Checkr (poll). Updates backend and returns latest status.
   * If report is complete, backend marks the background_search step complete.
   */
  refreshStatus: async (employeeId: string) => {
    const response = await apiClient.create(
      `${HR_API_BASE}/integrations/checkr/refresh-status/`,
      { employee_id: employeeId }
    );
    return response.data ?? response;
  },
};

// ==================== QuickBooks API ====================
export const quickbooksApi = {
  // Get QuickBooks OAuth authorization URL from backend
  connect: async () => {
    const response = await apiClient.get('/assignments/connect/', null);
    return response.data || response;
  },

  // Check QuickBooks connection status
  checkStatus: async () => {
    const response = await apiClient.get('/assignments/check-status/', null);
    return response.data || response;
  },

  // Create employee in QuickBooks
  createEmployee: async (employee_id: string) => {
    const response = await apiClient.create('/hrapi/integrations/quickbooks/create-employee/', {
      employee_id,
    });
    return response.data || response;
  },

  // Inactivate employee in QuickBooks
  inactivateEmployee: async (employee_id: string) => {
    const response = await apiClient.create('/hrapi/integrations/quickbooks/inactivate-employee/', {
      employee_id,
    });
    return response.data || response;
  },
};